import { useEffect } from 'react';
import { useAccount, useNetwork } from 'wagmi';

import { useAuthActions } from '#/features/auth/auth-context';
import { useWalletView } from '#/features/wallet/wallet-context';
import EthereumWallet from '#/features/ethereum-wallet/wallet';
import { logEvent, logException } from '#/features/logging/logging';

import usePrevious from '#/utils/usePrevious';

import WalletBadgeConnected from './WalletBadgeConnected';
import WalletBadgeConnect from './WalletBadgeConnect';
import WalletBadgeOnboarding from './WalletBadgeOnboarding';
import WalletBadgeRecover from './WalletBadgeRecover';

export default function Wallet() {
  const walletView = useWalletView();

  useGlobalEthereumEventHandlers();

  if (walletView.status === 'progress') {
    return <WalletBadgeOnboarding />;
  }

  if (walletView.nextSignInStep === 'wallet_connection') {
    return <WalletBadgeConnect />;
  }

  if (walletView.nextSignInStep === 'onboarding_and_authentication') {
    return <WalletBadgeRecover />;
  }

  return <WalletBadgeConnected />;
}

// WIP PTP-18887: Add E2E Playwright tests for this logic
function useGlobalEthereumEventHandlers() {
  const walletView = useWalletView();
  const authActions = useAuthActions();

  const { address: ethereumAddress } = useAccount();
  const prevEthereumAddress = usePrevious(ethereumAddress);
  useEffect(() => {
    if (!walletView.isSignedIn) return;
    if (ethereumAddress == null) return;
    if (prevEthereumAddress == null) return;
    if (prevEthereumAddress === ethereumAddress) return;

    logEvent('Signed-in user changed ethereum address', {
      prevEthereumAddress,
      ethereumAddress,
    });

    authActions.reset();
    EthereumWallet.disconnect()
      .catch((err) => {
        const message =
          'Unexpected error disconnecting wallet after user changed ethereum address';
        logException(new Error(message, { cause: err }));
      })
      .finally(() => {
        window.location.reload();
      });
  }, [
    authActions,
    ethereumAddress,
    prevEthereumAddress,
    walletView.isSignedIn,
  ]);

  const { chain } = useNetwork();
  const prevChainId = usePrevious(chain?.id);
  useEffect(() => {
    if (!walletView.isSignedIn) return;
    if (chain?.id == null) return;
    if (prevChainId == null) return;
    if (prevChainId === chain.id) return;

    logEvent('Signed-in user changed chain network', {
      prevChainId,
      chainId: chain.id,
    });

    authActions.reset();
    EthereumWallet.disconnect()
      .catch((err) => {
        const message =
          'Unexpected error disconnecting wallet after user changed chain network';
        logException(new Error(message, { cause: err }));
      })
      .finally(() => {
        window.location.reload();
      });
  }, [authActions, chain?.id, prevChainId, walletView.isSignedIn]);
}
